/**
 * Home's API calls. The follow-up, appointment and task types and calls moved to `followups.ts`, `appointments.ts` and
 * `tasks.ts` (app-leads-followups took them over). Home was written against `name: string`; the API can send `null`
 * for a lead with no name, so the wrappers below fill in "Unnamed" and keep Home's types as they were.
 */
import { fetchAppointments as fetchAppts, type AppointmentItem as ApiAppointment, type AppointmentsResult as ApiAppts } from "./appointments";
import type { ApiClient } from "./client";
import { fetchFollowups as fetchFus, type FollowupItem as ApiFollowup, type FollowupsResult as ApiFus, type Subject as ApiSubject } from "./followups";
import { listTasks, type TaskItem } from "./tasks";

export type { TaskItem } from "./tasks";
export type { AppointmentAction } from "./appointments";
export { doneFollowup, snoozeFollowup } from "./followups";
export { createTask, doneTask, reopenTask } from "./tasks";

export interface Subject extends Omit<ApiSubject, "name"> { name: string }
export interface FollowupItem extends Omit<ApiFollowup, "subject"> { subject: Subject }
export interface FollowupsResult extends Omit<ApiFus, "items"> { items: FollowupItem[] }
export interface AppointmentItem extends Omit<ApiAppointment, "person"> { person: Subject }
export interface AppointmentsResult extends Omit<ApiAppts, "items"> { items: AppointmentItem[] }

const subject = (s: ApiSubject): Subject => ({ ...s, name: s.name ?? "Unnamed" });

export async function fetchFollowups(c: ApiClient, s: { date: string; branchId?: string }): Promise<FollowupsResult> {
  const r = await fetchFus(c, s);
  return { ...r, items: r.items.map((i) => ({ ...i, subject: subject(i.subject) })) };
}
export async function fetchAppointments(c: ApiClient, s: { date: string; branchId?: string }): Promise<AppointmentsResult> {
  const r = await fetchAppts(c, s);
  return { ...r, items: r.items.map((i) => ({ ...i, person: subject(i.person) })) };
}
export { updateAppointment } from "./appointments";

export async function fetchTasks(c: ApiClient, s: { date: string; branchId?: string }): Promise<TaskItem[]> {
  return (await listTasks(c, { assignee: "me", date: s.date })).tasks;
}
